import { Box, Divider, Grid, Stack } from "@mui/material";
import { BankAccountActions } from "Components/BankAccounts/BankAccountDetail/BankAccountActions";
import { BankAccountBalance } from "Components/BankAccounts/BankAccountDetail/BankAccountBalance";
import { BankAccountDetailHeader } from "Components/BankAccounts/BankAccountDetail/BankAccountDetailHeader";
import { BankAccountDetailRow } from "Components/BankAccounts/BankAccountDetail/BankAccountDetailRow";
import { BankAccountDisconnect } from "Components/BankAccounts/BankAccountDetail/BankAccountDisconnect";
import { BankAccountProblemAlert } from "Components/BankAccounts/BankAccountDetail/BankAccountProblemAlert";
import { isBankAccountDetailDisabled } from "Components/BankAccounts/BankAccountDetail/disabledAccountsUtils";
import { useBankAccountProblemAlert } from "Components/BankAccounts/BankAccountDetail/useBankAccountProblemAlert";
import { VerificationStatusChip } from "Components/Shared/Account/VerificationStatusChip";
import { CopyToClipboard } from "Components/Shared/CopyToClipboard";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import type { BankAccountListItem } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { formatIBAN } from "Utils/AccountUtils";
import { formatDate } from "Utils/DateUtils";

const DETAIL_RESOURCES = Resources.Pages.Content.BankAccounts.Detail;
const COMMON_RESOURCES = Resources.Common;

type Props = {
  account: BankAccountListItem;
};

export const BankAccountDetailPageContent = ({ account }: Props) => {
  const { t } = useResource();
  const problemAlert = useBankAccountProblemAlert(account);
  const isDisabled = isBankAccountDetailDisabled(account);

  const {
    status,
    isReceiveOnly,
    bank,
    productName,
    iban,
    balance,
    currency,
    ownersNames,
    dateCreated,
  } = account;

  return (
    <Stack gap={3}>
      <PrimaryPaper sx={{ px: 4, py: 4.5 }}>
        <Stack>
          <Stack gap={3}>
            <BankAccountDetailHeader
              bank={bank}
              iban={iban}
              isDisabled={isDisabled}
              productName={productName}
            />
            {problemAlert && <BankAccountProblemAlert {...problemAlert} />}
            <BankAccountBalance
              currency={currency}
              balance={balance ?? null}
              isReceiveOnly={isReceiveOnly}
              isDisabled={isDisabled}
            />
          </Stack>
          <Divider sx={{ borderColor: "grey.100", my: 4 }} />
          <Grid
            container
            direction="row"
            spacing={3}
            sx={
              isDisabled ? { opacity: 0.5, pointerEvents: "none" } : undefined
            }
          >
            <BankAccountDetailRow
              left={t(COMMON_RESOURCES.Status)}
              right={<VerificationStatusChip verificationStatus={status} />}
            />
            <BankAccountDetailRow
              left={t(DETAIL_RESOURCES.IBAN)}
              right={
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  gap={0.5}
                  sx={{ wordBreak: "break-all" }}
                >
                  {formatIBAN(iban)}
                  <CopyToClipboard
                    text={iban}
                    size={18}
                    accessibilityLabel={t(DETAIL_RESOURCES.IbanCopySuccess)}
                    successMessage={t(DETAIL_RESOURCES.IbanCopySuccess)}
                  />
                </Box>
              }
            />
            <BankAccountDetailRow
              left={t(DETAIL_RESOURCES.AccountOwners)}
              right={ownersNames.length > 0 ? ownersNames.join(", ") : "-"}
            />
            <BankAccountDetailRow
              left={t(DETAIL_RESOURCES.DateConnected)}
              right={formatDate(dateCreated)}
            />
          </Grid>
        </Stack>
      </PrimaryPaper>
      <PrimaryPaper sx={{ p: { xs: 0, md: "10px" }, overflow: "hidden" }}>
        <BankAccountActions account={account} />
      </PrimaryPaper>
      <PrimaryPaper sx={{ p: { xs: 0, md: "10px" }, overflow: "hidden" }}>
        <BankAccountDisconnect account={account} />
      </PrimaryPaper>
    </Stack>
  );
};
